import React, { useContext } from "react";
import classNames from "classnames";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { StateAwareNavLink } from "@components/routes/StateAwareNavLink";
import { AccountingReportContext } from "../AccountingReport.context";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import {
    isConsolidatedTotalColumn,
    isEliminationsColumn,
} from "../consolidatedColumns";
import { getDrillDownClassParams } from "../useGLDrillDownUrl";
import { buildGlDrillDownParams, GlDrillDownParams } from "../glDrillDown.lib";
import { buildEliminationDetailUrl } from "../buildEliminationDetailUrl";
import styles from "./BalanceSheetTable.module.scss";

interface Props {
    value: number;
    accountCodes: string[];
    period: ReportPeriodWithLabel;
    isTotal: boolean;
    isGroupEnd?: boolean;
    tooltip?: string;
    onDrillDown?: (params: GlDrillDownParams) => void;
}

/**
 * ViewTable-compatible Balance Sheet value cell.
 * Renders as a <div> (not <td>) for CSS-grid row compatibility.
 */
export const BalanceSheetViewValueCell: React.FC<Props> = React.memo(
    ({
        value,
        accountCodes,
        period,
        isTotal,
        isGroupEnd,
        tooltip,
        onDrillDown,
    }) => {
        const { filters } = useContext(AccountingReportContext);
        const { text: displayValue, negativeClassName } =
            useReportAmountDisplay(value, {
                comparisonDiff: period.comparisonDiff,
            });
        const isDifferenceColumn = !!period.comparisonDiff;
        const isEliminations = isEliminationsColumn(period);
        const isConsolidatedColumn =
            isEliminations || isConsolidatedTotalColumn(period);
        const hasDrillDown = accountCodes.length > 0 && !isConsolidatedColumn;
        const maybeAccountCode =
            accountCodes.length === 1 ? Number(accountCodes[0]) : undefined;
        const accountCode =
            maybeAccountCode !== undefined && Number.isFinite(maybeAccountCode)
                ? maybeAccountCode
                : undefined;

        const eliminationDetailUrl = buildEliminationDetailUrl({
            startDate: period.startDate,
            endDate: period.endDate,
            workspaceId: filters.workspaceId,
            consolidationGroupId: filters.consolidationGroupId,
            accountCode,
        });

        const drillDownParams = buildGlDrillDownParams({
            startDate: period.startDate,
            endDate: period.endDate,
            entityId: period.entityId ?? filters.entityId,
            ledgerId: filters.ledgerId,
            accountCodes,
            ...getDrillDownClassParams(period, filters),
            counterpartiesIds: filters.counterpartiesIds,
            withEmptyOrUnverifiedCounterparty:
                filters.withEmptyOrUnverifiedCounterparty,
            hasCounterparty: filters.hasCounterparty,
            accountIds: filters.accountIds,
            subaccountIds: filters.subaccountIds,
        });

        const shouldRenderGlButton =
            hasDrillDown && !isDifferenceColumn && onDrillDown !== undefined;
        const shouldRenderEliminationLink =
            Boolean(value) &&
            !isDifferenceColumn &&
            isEliminations &&
            Boolean(filters.consolidationGroupId) &&
            Boolean(filters.workspaceId);

        let cellContent: React.ReactNode = tooltip ? (
            <Tooltip content={tooltip} side="bottom">
                <span>{displayValue}</span>
            </Tooltip>
        ) : (
            displayValue
        );
        if (shouldRenderGlButton && onDrillDown) {
            cellContent = (
                <button
                    type="button"
                    className={classNames(
                        "data-table__drill-down",
                        styles.drillDownButton,
                    )}
                    onClick={() => onDrillDown(drillDownParams)}
                >
                    {displayValue}
                </button>
            );
        } else if (shouldRenderEliminationLink) {
            cellContent = (
                <StateAwareNavLink
                    to={eliminationDetailUrl}
                    className="data-table__drill-down"
                >
                    {displayValue}
                </StateAwareNavLink>
            );
        }

        return (
            <ViewTableCell
                className={classNames(styles.numericCell, negativeClassName, {
                    [styles.totalCell]: isTotal,
                    [styles.groupEndCell]: isGroupEnd,
                })}
            >
                {cellContent}
            </ViewTableCell>
        );
    },
);
